/**
 * Nichos Virais V2 - Sistema Inteligente de Canais
 * ================================================
 * Build v2.2 - Added Crescendo tab - Force rebuild 1736106555
 * 
 * Abas Dinâmicas:
 * 🚀 Explodindo: < 15 dias desde 1º post E > 100k views
 * 📈 Em Alta: 15-60 dias de vida E > 500k views
 * 🌱 < 1000 Inscritos: Todos com menos de 1000 subs
 * 🗂️ Todos os Nichos: Todos os canais agrupados por categoria
 * ❌ Excluídos: Canais removidos do YouTube
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Flame, TrendingUp, Users, Upload, RefreshCw,
  ExternalLink, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Search, FolderOpen,
  Loader, AlertCircle, X, FileJson, Zap, Trash2, DollarSign, Plus, Link, Archive, Eye
} from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import Sidebar from './Sidebar';
import Header from './Header';
import PremiumFeatureBlock from './ui/PremiumFeatureBlock';

const API = import.meta.env.VITE_BACKEND_URL + '/api';

// Category translation keys for partial matching
const CATEGORY_KEYS = [
  'Automotive Business', 'Automotive', 'Aviation', 'Celebrity', 'History', 'Physics',
  'Politics', 'Science', 'Technology', 'Gaming', 'Music', 'Sports', 'Entertainment',
  'Education', 'News', 'Comedy', 'Film', 'Animation', 'Howto', 'How-to', 'DIY',
  'Travel', 'Food', 'Cooking', 'Fashion', 'Beauty', 'Health', 'Fitness', 'Business',
  'Finance', 'Crypto', 'Cryptocurrency', 'Art', 'Design', 'Photography', 'Nature',
  'Animals', 'Pets', 'Cars', 'Motorcycles', 'Real Estate', 'Lifestyle', 'Motivation',
  'Self-Improvement', 'Psychology', 'Philosophy', 'Religion', 'Spirituality', 'Paranormal',
  'Mystery', 'True Crime', 'Documentary', 'Reaction', 'Commentary', 'Review', 'Unboxing',
  'ASMR', 'Relaxation', 'Kids', 'Family', 'Parenting', 'Wedding', 'Military', 'Space',
  'Astronomy', 'Geography', 'Economics', 'Law', 'Medicine', 'Engineering', 'Architecture',
  'Mathematics', 'Chemistry', 'Biology', 'Linguistics', 'Languages'
];

// Função para traduzir categoria usando i18n
const translateCategory = (category, t) => {
  if (!category) return t('viralNiches.no_category');

  // Remove emoji do início se houver
  const cleanCategory = category.replace(/^[📁🔬🎮🎬🎵⚽🎨💼🔧🎯🌍🐾🚗✈️🏠💡🎭📚🔮👶💒🎖️🚀📊⚖️💊🔩🏛️➗🧪🧬🗣️]\s*/, '');

  // Procura tradução exata via i18n
  if (cleanCategory) {
    const exactKey = `viralNiches.categories.${cleanCategory}`;
    const exactTranslation = t(exactKey, { defaultValue: '' });
    if (exactTranslation && typeof exactTranslation === 'string') {
      return exactTranslation;
    }
  }

  // Procura tradução parcial (case insensitive)
  const lowerCategory = cleanCategory.toLowerCase();
  for (const key of CATEGORY_KEYS) {
    if (lowerCategory.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerCategory)) {
      const result = t(`viralNiches.categories.${key}`, { defaultValue: cleanCategory });
      return typeof result === 'string' ? result : String(result);
    }
  }

  // Se não encontrou tradução, retorna o original sem emoji
  return cleanCategory || String(t('viralNiches.no_category'));
};

// Helper function to ensure URL has protocol
const ensureHttps = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SISTEMA DE CANAIS VISITADOS (localStorage)
// ═══════════════════════════════════════════════════════════════════════════════

const VISITED_CHANNELS_KEY = 'nichos_virais_visited_channels';

// Carrega lista de canais visitados do localStorage
const getVisitedChannels = () => {
  try {
    const stored = localStorage.getItem(VISITED_CHANNELS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Salva canal como visitado
const markChannelAsVisited = (channelId) => {
  try {
    const visited = getVisitedChannels();
    if (!visited.includes(channelId)) {
      visited.push(channelId);
      localStorage.setItem(VISITED_CHANNELS_KEY, JSON.stringify(visited));
    }
    return visited;
  } catch {
    return [];
  }
};

// Verifica se canal foi visitado
const isChannelVisited = (channelId, visitedList) => {
  return visitedList.includes(channelId);
};

// ═══════════════════════════════════════════════════════════════════════════════

// Helper function to get channel thumbnail
const getChannelThumbnail = (channel) => {
  if (channel.thumbnail && channel.thumbnail.length > 10) {
    return channel.thumbnail;
  }
  const initial = (channel.title || 'C')[0].toUpperCase();
  return `https://ui-avatars.com/api/?name=${initial}&background=random&size=176&bold=true`;
};

// Tab definitions with icons and filters (labels/descriptions resolved via t() at render time)
const TAB_CONFIGS = [
  { id: 'exploding', labelKey: 'viralNiches.exploding', icon: Zap, color: '#EF4444', descKey: 'viralNiches.tab_exploding_desc' },
  { id: 'rising', labelKey: 'viralNiches.trending', icon: TrendingUp, color: '#F59E0B', descKey: 'viralNiches.tab_rising_desc' },
  { id: 'growing', labelKey: 'viralNiches.growing', icon: TrendingUp, color: '#06B6D4', descKey: 'viralNiches.tab_growing_desc' },
  { id: 'new', labelKey: 'viralNiches.new_channels', icon: Flame, color: '#8B5CF6', descKey: 'viralNiches.tab_new_desc' },
  { id: 'others', labelKey: 'viralNiches.others', icon: Archive, color: '#6B7280', descKey: 'viralNiches.tab_others_desc' },
  { id: 'all', labelKey: 'viralNiches.all_niches', icon: FolderOpen, color: '#3B82F6', descKey: 'viralNiches.tab_all_desc' },
];

// Helper to resolve tabs with translations
const useTabs = (t) => TAB_CONFIGS.map(tab => ({
  ...tab,
  label: t(tab.labelKey),
  description: t(tab.descKey),
}));

const VALID_TAB_IDS = TAB_CONFIGS.map(t => t.id);

const NichosViraisV2Page = ({ onLogout, isSuperAdmin }) => {
  usePageTitle('Nichos Virais');
  useTheme(); // subscribe to theme changes for CSS variable reactivity
  const { sidebarWidth, isMobile } = useSidebar();
  const { isPrivate } = usePrivacy();
  const { t } = useTranslation();
  const TABS = useTabs(t);

  // Ler tab da URL (?tab=exploding) — usado quando o Radar do Dashboard direciona
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && VALID_TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'exploding';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [channels, setChannels] = useState([]);
  const [groupedChannels, setGroupedChannels] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [page, setPage] = useState(1);
  const [totalChannels, setTotalChannels] = useState(0);
  const CHANNELS_PER_PAGE = 200;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [accessCheck, setAccessCheck] = useState({ loading: true, hasAccess: false, message: '' });
  
  // Estado de canais visitados (persiste no localStorage)
  const [visitedChannels, setVisitedChannels] = useState(() => getVisitedChannels());
  
  // Estado para adicionar canal via URL
  const [showAddChannelModal, setShowAddChannelModal] = useState(false);
  const [addChannelUrl, setAddChannelUrl] = useState('');
  const [addChannelCategory, setAddChannelCategory] = useState('Outros/Não Classificado');
  const [addingChannel, setAddingChannel] = useState(false);
  
  // Estado para corrigir datas / full refresh
  
  // Handler para marcar canal como visitado
  const handleChannelVisit = (channelId) => {
    const updated = markChannelAsVisited(channelId);
    setVisitedChannels([...updated]);
  };
  
  // Handler para adicionar canal via URL
  const handleAddChannel = async () => {
    if (!addChannelUrl.trim()) {
      toast.error(t('viralNiches.enter_channel_url'));
      return;
    }
    
    setAddingChannel(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/nichos-virais/add-channel`, {
        url: addChannelUrl.trim(),
        category: addChannelCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowAddChannelModal(false);
        setAddChannelUrl('');
        fetchChannels();
        fetchStats();
      } else {
        toast.error(response.data.message || t('viralNiches.error_add_channel'));
      }
    } catch (error) {
      console.error('Erro ao adicionar canal:', error);
      toast.error(error.response?.data?.detail || t('viralNiches.error_add_channel'));
    } finally {
      setAddingChannel(false);
    }
  };

  // Check access
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/auth/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.is_super_admin) {
          setAccessCheck({ loading: false, hasAccess: true, message: '' });
          return;
        }
        
        setAccessCheck({
          loading: false,
          hasAccess: response.data.nichos_virais_acesso || false,
          message: response.data.nichos_virais_mensagem || t('viralNiches.resource_blocked_plan')
        });
      } catch (error) {
        console.error('Error checking access:', error);
        setAccessCheck({ loading: false, hasAccess: false, message: t('viralNiches.error_check_access') });
      }
    };
    checkAccess();
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/nichos-virais/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch channels based on active tab
  const fetchChannels = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (activeTab === 'all') {
        const response = await axios.get(`${API}/nichos-virais/channels/grouped`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGroupedChannels(response.data);
        setChannels([]);
        setTotalChannels(0);
      } else {
        const offset = (pageNum - 1) * CHANNELS_PER_PAGE;
        const response = await axios.get(`${API}/nichos-virais/channels?filter=${activeTab}&limit=${CHANNELS_PER_PAGE}&offset=${offset}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChannels(response.data.channels || []);
        setTotalChannels(response.data.total || 0);
        setGroupedChannels({});
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error(t('viralNiches.error_load_channels'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (accessCheck.hasAccess) {
      fetchStats();
      fetchChannels();
    }
  }, [accessCheck.hasAccess, fetchStats, fetchChannels]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error(t('viralNiches.select_json_file'));
      return;
    }

    setUploading(true);
    setShowUploadModal(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/nichos-virais/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status === 'already_running') {
        toast.info('Importação já está em andamento');
      } else {
        toast.success(`Importação de ${response.data.total || ''} canais iniciada em background`);
      }

      // Polling do progresso a cada 3 segundos
      const pollInterval = setInterval(async () => {
        try {
          const statusResp = await axios.get(`${API}/nichos-virais/import/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const task = statusResp.data;

          if (!task.running) {
            clearInterval(pollInterval);
            setUploading(false);
            if (task.inserted > 0 || task.updated > 0 || task.errors > 0) {
              toast.success(
                `Importação concluída: ${task.inserted} novos, ${task.updated} atualizados` +
                (task.errors > 0 ? `, ${task.errors} erros` : ''),
                { duration: 5000 }
              );
            }
            fetchStats();
            fetchChannels();
          }
        } catch (e) {
          clearInterval(pollInterval);
          setUploading(false);
        }
      }, 3000);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || t('viralNiches.error_import_file'));
      setUploading(false);
    }
  };

  // Handle manual update
  const handleUpdateAll = async () => {
    setUpdating(true);
    
    const toastId = toast.loading(t('viralNiches.updating_channels_api'), {
      description: t('viralNiches.checking_channels_update')
    });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/nichos-virais/update`, {
        max_channels: 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.dismiss(toastId);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStats();
        fetchChannels();
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Error updating channels:', error);
      toast.error(error.response?.data?.detail || t('viralNiches.error_update_channels'));
    } finally {
      setUpdating(false);
    }
  };

  // Handle channel delete
  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm(t('viralNiches.confirm_delete_channel'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nichos-virais/channels/${channelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('viralNiches.channel_deleted_success'));
      fetchStats();
      fetchChannels();
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast.error(t('viralNiches.error_delete_channel'));
    }
  };

  // Format numbers
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Filter channels by search
  const filteredChannels = channels.filter(channel => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      channel.title?.toLowerCase().includes(search) ||
      channel.channel_url?.toLowerCase().includes(search) ||
      channel.primary_category?.toLowerCase().includes(search)
    );
  });

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Access check loading/blocked states
  if (accessCheck.loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          transition: 'margin-left 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader size={48} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
        </main>
      </div>
    );
  }

  if (!accessCheck.hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <PremiumFeatureBlock />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
      
      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />
      
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Header Section */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'rgba(13, 174, 255, 0.08)',
                border: '1px solid rgba(13, 174, 255, 0.1)',
                borderRadius: '12px',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Flame size={22} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div>
                <h1 style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: 'var(--dp-text-primary)',
                  margin: 0
                }}>
                  {t('viralNiches.title')}
                </h1>
                <p style={{
                  color: 'var(--dp-text-secondary)',
                  fontSize: '14px',
                  margin: '4px 0 0 0'
                }}>
                  {t('viralNiches.intelligent_system')}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {isSuperAdmin && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowUploadModal(true)}
                  disabled={uploading}
                  data-testid="import-json-btn"
                  style={{
                    padding: '10px 20px',
                    background: uploading ? 'var(--dp-border-subtle)' : 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: uploading ? 0.7 : 1
                  }}
                >
                  {uploading ? <Loader size={18} className="animate-spin" /> : <Upload size={18} />}
                  {uploading ? t('viralNiches.processing') : t('viralNiches.import_json')}
                </button>
                
                <button
                  onClick={handleUpdateAll}
                  disabled={updating}
                  data-testid="update-all-btn"
                  style={{
                    padding: '10px 20px',
                    background: updating ? 'var(--dp-border-subtle)' : 'linear-gradient(135deg, #10B981, #059669)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: updating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: updating ? 0.7 : 1
                  }}
                >
                  <RefreshCw size={18} className={updating ? 'animate-spin' : ''} />
                  {updating ? t('viralNiches.updating') : t('viralNiches.update_data')}
                </button>
                
                <button
                  onClick={() => setShowAddChannelModal(true)}
                  data-testid="add-channel-btn"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={18} />
                  {t('viralNiches.add_channel')}
                </button>
                
              </div>
            )}
          </div>

          {/* Stats Cards */}
          {stats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '14px',
              marginTop: '20px'
            }}>
              {TABS.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                <div
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setPage(1); }}
                  data-testid={`tab-stat-${tab.id}`}
                  style={{
                    background: isActive
                      ? `linear-gradient(135deg, ${tab.color}18, ${tab.color}08)`
                      : 'var(--dp-bg-card)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: `1px solid ${isActive ? tab.color + '60' : 'var(--dp-border-subtle)'}`,
                    borderRadius: '14px',
                    padding: '18px 16px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isActive
                      ? `0 4px 20px ${tab.color}15`
                      : '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = isActive
                      ? `0 8px 24px ${tab.color}25`
                      : '0 8px 20px rgba(0,0,0,0.15)';
                    if (!isActive) e.currentTarget.style.borderColor = tab.color + '40';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = isActive
                      ? `0 4px 20px ${tab.color}15`
                      : '0 2px 8px rgba(0,0,0,0.05)';
                    if (!isActive) e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: `${tab.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 10px auto',
                  }}>
                    <tab.icon size={18} style={{ color: tab.color }} />
                  </div>
                  <p style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'var(--dp-text-primary)',
                    margin: '0 0 4px 0',
                    letterSpacing: '-0.5px',
                    fontFamily: 'var(--dp-font)',
                  }}>
                    {formatNumber(stats.filters[tab.id] || 0)}
                  </p>
                  <p style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: isActive ? tab.color : 'var(--dp-text-secondary)',
                    margin: 0,
                    fontFamily: 'var(--dp-font)',
                  }}>
                    {tab.label}
                  </p>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tab Description */}
        <div style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '10px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          {(() => {
            const currentTab = TABS.find(t => t.id === activeTab);
            return (
              <>
                <currentTab.icon size={18} style={{ color: currentTab.color }} />
                <span style={{ color: 'var(--dp-text-secondary)', fontSize: '13px' }}>
                  <strong style={{ color: 'var(--dp-text-primary)' }}>{currentTab.label}:</strong> {currentTab.description}
                </span>
              </>
            );
          })()}
        </div>

        {/* Search Bar */}
        <div style={{
          marginBottom: '20px',
          position: 'relative'
        }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--dp-text-secondary)'
          }} />
          <input
            type="text"
            placeholder={t('viralNiches.search_channels')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
            style={{
              width: '100%',
              padding: '14px 14px 14px 44px',
              background: 'var(--dp-bg-input)',
              color: 'var(--dp-text-primary)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '12px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--dp-text-secondary)',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Content Area */}
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px',
            background: 'var(--dp-bg-card)',
            borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)'
          }}>
            <Loader size={40} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
          </div>
        ) : activeTab === 'all' ? (
          // Grouped by Category View
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.keys(groupedChannels).length === 0 ? (
              <EmptyState isSuperAdmin={isSuperAdmin} onUpload={() => setShowUploadModal(true)} />
            ) : (
              Object.entries(groupedChannels)
                .filter(([, data]) => data && typeof data === 'object' && !Array.isArray(data))
                .sort((a, b) => (Number(b[1].count) || 0) - (Number(a[1].count) || 0))
                .map(([category, data]) => (
                  <CategorySection
                    key={category}
                    category={category}
                    data={data}
                    expanded={expandedCategories[category]}
                    onToggle={() => toggleCategory(category)}
                    formatNumber={formatNumber}
                    searchTerm={searchTerm}
                    isSuperAdmin={isSuperAdmin}
                    onDelete={handleDeleteChannel}
                    visitedChannels={visitedChannels}
                    onVisit={handleChannelVisit}
                    isPrivate={isPrivate}
                  />
                ))
            )}
          </div>
        ) : (
          // Filtered Channels Grid
          <div>
            {filteredChannels.length === 0 ? (
              <EmptyState
                isSuperAdmin={isSuperAdmin}
                onUpload={() => setShowUploadModal(true)}
                isRemovedTab={activeTab === 'removed'}
              />
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '16px'
              }}>
                {filteredChannels.map(channel => (
                  <ChannelCard
                    key={channel.id}
                    channel={channel}
                    formatNumber={formatNumber}
                    activeTab={activeTab}
                    isSuperAdmin={isSuperAdmin}
                    onDelete={handleDeleteChannel}
                    isVisited={isChannelVisited(channel.channel_id, visitedChannels)}
                    onVisit={handleChannelVisit}
                    isPrivate={isPrivate}
                  />
                ))}
              </div>
            )}
            {/* Pagination */}
            {totalChannels > CHANNELS_PER_PAGE && !searchTerm && (
              (() => {
                const totalPages = Math.ceil(totalChannels / CHANNELS_PER_PAGE);
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px', marginTop: '24px', flexWrap: 'wrap',
                  }}>
                    <button
                      onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchChannels(p); }}
                      disabled={page <= 1}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: page <= 1 ? 'not-allowed' : 'pointer',
                        background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                        color: page <= 1 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-primary)',
                        opacity: page <= 1 ? 0.5 : 1, transition: 'all 0.2s',
                      }}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => { setPage(p); fetchChannels(p); }}
                        style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                          fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                          background: p === page ? 'linear-gradient(135deg, #0DAEFF, #0B8FDB)' : 'var(--dp-bg-card)',
                          border: p === page ? '1px solid #0DAEFF' : '1px solid var(--dp-border-subtle)',
                          color: p === page ? '#fff' : 'var(--dp-text-secondary)',
                          boxShadow: p === page ? '0 0 12px rgba(13,174,255,0.25)' : 'none',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchChannels(p); }}
                      disabled={page >= totalPages}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                        background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                        color: page >= totalPages ? 'var(--dp-text-tertiary)' : 'var(--dp-text-primary)',
                        opacity: page >= totalPages ? 0.5 : 1, transition: 'all 0.2s',
                      }}
                    >
                      Próxima
                    </button>
                    <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginLeft: '8px' }}>
                      {((page - 1) * CHANNELS_PER_PAGE) + 1}–{Math.min(page * CHANNELS_PER_PAGE, totalChannels)} de {totalChannels}
                    </span>
                  </div>
                );
              })()
            )}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadModal
            uploading={uploading}
            onUpload={handleFileUpload}
            onClose={() => setShowUploadModal(false)}
          />
        )}
        
        {/* Modal para Adicionar Canal por URL */}
        {showAddChannelModal && (
          <div className="dp-modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            animation: 'dp-modal-overlay 0.2s ease'
          }} onClick={() => {
            setShowAddChannelModal(false);
            setAddChannelUrl('');
          }}>
            <div style={{
              background: 'var(--dp-bg-glass)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              borderRadius: '16px',
              padding: '28px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid var(--dp-border-medium)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
              animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Link size={20} />
                  {t('viralNiches.add_channel_by_url')}
                </h3>
                <button
                  onClick={() => {
                    setShowAddChannelModal(false);
                    setAddChannelUrl('');
                  }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    border: '1px solid var(--dp-border-subtle)',
                    background: 'transparent',
                    color: 'var(--dp-text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', fontFamily: 'var(--dp-font)' }}>
                  {t('viralNiches.youtube_channel_url')}
                </label>
                <input
                  type="text"
                  value={addChannelUrl}
                  onChange={(e) => setAddChannelUrl(e.target.value)}
                  placeholder={t('viralNiches.url_placeholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--dp-bg-input)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: 'var(--dp-text-primary)',
                    outline: 'none',
                    fontFamily: 'var(--dp-font)'
                  }}
                />
                <p style={{ color: 'var(--dp-text-secondary)', fontSize: '12px', marginTop: '8px', fontFamily: 'var(--dp-font)' }}>
                  {t('viralNiches.accepted_formats')}
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', fontFamily: 'var(--dp-font)' }}>
                  {t('viralNiches.category')}
                </label>
                <input
                  type="text"
                  value={addChannelCategory}
                  onChange={(e) => setAddChannelCategory(e.target.value)}
                  placeholder={t('viralNiches.category_placeholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--dp-bg-input)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: 'var(--dp-text-primary)',
                    outline: 'none',
                    fontFamily: 'var(--dp-font)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddChannelModal(false);
                    setAddChannelUrl('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--dp-bg-input)',
                    color: 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--dp-font)'
                  }}
                >
                  {t('viralNiches.cancel')}
                </button>
                <button
                  onClick={handleAddChannel}
                  disabled={addingChannel || !addChannelUrl.trim()}
                  style={{
                    padding: '10px 20px',
                    background: addingChannel || !addChannelUrl.trim()
                      ? 'var(--dp-border-subtle)'
                      : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600))',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    cursor: addingChannel || !addChannelUrl.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'var(--dp-font)'
                  }}
                >
                  {addingChannel ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      {t('viralNiches.adding')}
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      {t('viralNiches.add_channel')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Channel Card Component
const ChannelCard = ({ channel, formatNumber, activeTab, isSuperAdmin, onDelete, isVisited, onVisit, isPrivate }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();
  const TABS = useTabs(t);

  const tabConfig = TABS.find(tb => tb.id === activeTab) || TABS[0];
  const isRemoved = channel.is_removed_from_youtube;
  
  // Get top 3 videos
  const topVideos = (channel.top_videos || []).slice(0, 3);
  const hasVideos = topVideos.length > 0;
  const hasMultipleVideos = topVideos.length > 1;
  
  const nextVideo = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentVideoIndex((prev) => (prev + 1) % topVideos.length);
  };
  
  const prevVideo = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentVideoIndex((prev) => (prev - 1 + topVideos.length) % topVideos.length);
  };
  
  // Handler para quando clicar em "Ver Canal"
  const handleViewChannel = () => {
    onVisit(channel.channel_id);
  };
  
  return (
    <div 
      data-testid={`channel-card-${channel.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'var(--dp-bg-card)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${isHovered && !isRemoved ? tabConfig.color + '40' : 'var(--dp-border-subtle)'}`,
        borderRadius: '14px',
        overflow: 'hidden',
        opacity: isRemoved ? 0.5 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered && !isRemoved ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered && !isRemoved
          ? `0 16px 32px -8px rgba(0,0,0,0.3), 0 0 0 1px ${tabConfig.color}15`
          : '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      {/* Header - Compact */}
      <div style={{
        padding: '12px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
      }}>
        <img
          src={isPrivate ? `https://ui-avatars.com/api/?name=?&background=888888&size=176&bold=true` : getChannelThumbnail(channel)}
          alt={isPrivate ? t('viralNiches.view_channel') : channel.title}
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${isPrivate ? '?' : (channel.title || 'C')[0]}&background=random&size=176&bold=true`;
          }}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: `2px solid ${tabConfig.color}30`,
            filter: isRemoved ? 'grayscale(100%)' : (isPrivate ? 'blur(2px)' : 'none'),
            transition: 'all 0.3s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <a
              href={!isPrivate && channel.channel_url ? ensureHttps(channel.channel_url) : '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (isPrivate) {
                  e.preventDefault();
                  toast.info(t('viralNiches.disable_private_mode'));
                } else {
                  handleViewChannel();
                }
              }}
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                textDecoration: 'none',
                cursor: isPrivate ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => !isPrivate && (e.target.style.color = tabConfig.color)}
              onMouseLeave={(e) => e.target.style.color = 'var(--dp-text-primary)'}
            >
              {isPrivate ? '••••••••••' : (channel.title || t('viralNiches.channel_no_name'))}
            </a>
            {channel.is_monetized && (
              <span style={{
                fontSize: '9px',
                padding: '2px 5px',
                background: '#10B98115',
                color: '#10B981',
                borderRadius: '8px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <DollarSign size={8} />
                $
              </span>
            )}
            {isRemoved && (
              <span style={{
                fontSize: '8px',
                padding: '2px 6px',
                background: '#EF444420',
                color: '#EF4444',
                borderRadius: '10px',
                fontWeight: '700'
              }}>
                {t('viralNiches.deleted_badge')}
              </span>
            )}
          </div>
          
          {/* Categories */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
            {(channel.categories && Array.isArray(channel.categories) && channel.categories.length > 0
              ? channel.categories.slice(0, 2)
              : channel.primary_category ? [channel.primary_category] : []
            ).map((cat, idx) => (
              <span key={idx} style={{
                fontSize: '9px',
                padding: '2px 6px',
                background: `${tabConfig.color}15`,
                color: tabConfig.color,
                borderRadius: '6px',
                fontWeight: '500',
                textTransform: 'capitalize'
              }}>
                {isPrivate ? '•••••' : String(typeof cat === 'string' ? cat : (cat?.name || cat?.[0] || JSON.stringify(cat))).replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}]+\s*/u, '')}
              </span>
            ))}
          </div>
        </div>
        
        {/* Delete button for admin */}
        {isSuperAdmin && !isRemoved && (
          <button
            onClick={() => onDelete(channel.id)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              opacity: isHovered ? 0.8 : 0.3,
              transition: 'opacity 0.2s'
            }}
            title={t('viralNiches.delete_channel_title')}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Video Carousel - Only if has videos */}
      {hasVideos && (
        <div style={{ 
          position: 'relative',
          margin: '0 12px',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          {/* Current Video Thumbnail - Fixed height like Canais Removidos */}
          <div style={{ 
            position: 'relative',
            width: '100%',
            height: '140px',
            background: '#1a1a1a'
          }}>
            <img
              src={isPrivate ? 'https://placehold.co/320x180/888888/ffffff?text=?' : topVideos[currentVideoIndex]?.video_thumbnail_url}
              alt={isPrivate ? t('viralNiches.video') : topVideos[currentVideoIndex]?.video_title}
              onError={(e) => {
                e.target.src = 'https://placehold.co/320x180/374151/9CA3AF?text=Video';
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'all 0.4s ease'
              }}
            />
            
            {/* Video Duration - Bottom Left */}
            {topVideos[currentVideoIndex]?.length_text && (
              <span style={{
                position: 'absolute',
                bottom: '6px',
                left: '6px',
                background: 'rgba(0,0,0,0.85)',
                color: 'white',
                padding: '2px 5px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
                fontFamily: 'monospace'
              }}>
                {topVideos[currentVideoIndex].length_text}
              </span>
            )}
            
            {/* Video Views Badge */}
            <span style={{
              position: 'absolute',
              bottom: '6px',
              right: '6px',
              background: 'rgba(0,0,0,0.85)',
              color: 'white',
              padding: '2px 5px',
              borderRadius: '4px',
              fontSize: '9px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              <Eye size={10} />
              {isPrivate ? '•••' : formatNumber(topVideos[currentVideoIndex]?.video_view_count)}
            </span>

            {/* Navigation Arrows */}
            {hasMultipleVideos && (
              <>
                <button
                  onClick={prevVideo}
                  style={{
                    position: 'absolute',
                    left: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isHovered ? 1 : 0,
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={nextVideo}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isHovered ? 1 : 0,
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <ChevronRight size={14} />
                </button>
              </>
            )}
            
            {/* Video Indicators */}
            {hasMultipleVideos && (
              <div style={{
                position: 'absolute',
                top: '6px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '4px'
              }}>
                {topVideos.map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: idx === currentVideoIndex ? '14px' : '5px',
                      height: '5px',
                      borderRadius: '3px',
                      background: idx === currentVideoIndex ? 'white' : 'rgba(255,255,255,0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Video Title */}
          <div style={{
            padding: '8px',
            background: 'var(--dp-bg-input)'
          }}>
            <p style={{
              fontSize: '11px',
              color: 'var(--dp-text-primary)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: '500'
            }}>
              {isPrivate ? '•••••••••••••' : String(topVideos[currentVideoIndex]?.video_title || '')}
            </p>
          </div>
        </div>
      )}

      {/* Stats Row - Compact */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        padding: '12px 14px',
        margin: hasVideos ? '10px 12px 0' : '0 12px',
        marginTop: hasVideos ? '10px' : '0',
        background: 'var(--dp-bg-elevated, rgba(255,255,255,0.02))',
        borderRadius: '10px',
        border: '1px solid var(--dp-border-subtle)',
      }}>
        <MiniStatBox icon={<Eye size={14} />} value={isPrivate ? '•••' : formatNumber(channel.total_views)} label={t('viralNiches.views_label')} />
        <MiniStatBox icon={<Users size={14} />} value={isPrivate ? '•••' : formatNumber(channel.subscribers)} label={t('viralNiches.subs_label')} />
        <MiniStatBox icon={<Upload size={14} />} value={isPrivate ? '•••' : (channel.num_uploads || 0)} label={t('viralNiches.videos_label')} />
      </div>

      {/* Footer - Days posting + Link (original layout) */}
      <div style={{
        padding: '12px 14px',
        borderTop: '1px solid var(--dp-border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '4px',
      }}>
        {/* Days posting */}
        <span style={{
          fontSize: '11px',
          color: 'var(--dp-text-tertiary)',
          fontWeight: 500,
          fontFamily: 'var(--dp-font)',
        }}>
          {channel.posting_age_days !== undefined && channel.posting_age_days !== null
            ? `📅 ${isPrivate ? '•••' : channel.posting_age_days} ${t('viralNiches.days_posting')}`
            : ''
          }
        </span>

        {/* Link button */}
        {!isRemoved && (
          <a
            href={isPrivate ? '#' : ensureHttps(channel.channel_url)}
            target={isPrivate ? '_self' : '_blank'}
            rel="noopener noreferrer"
            data-testid={`view-channel-${channel.id}`}
            onMouseDown={isPrivate ? (e) => e.preventDefault() : handleViewChannel}
            onClick={isPrivate ? (e) => { e.preventDefault(); toast.info(t('viralNiches.disable_private_mode')); } : undefined}
            style={{
              padding: '7px 14px',
              background: isPrivate
                ? 'var(--dp-text-tertiary)'
                : isVisited
                  ? 'var(--dp-text-tertiary)'
                  : `linear-gradient(135deg, ${tabConfig.color}, ${tabConfig.color}CC)`,
              color: 'white',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'var(--dp-font)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              opacity: isPrivate ? 0.5 : (isVisited ? 0.7 : 1),
              cursor: isPrivate ? 'not-allowed' : 'pointer',
              transition: 'all 0.25s ease',
              boxShadow: (!isPrivate && !isVisited)
                ? `0 2px 8px ${tabConfig.color}30`
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (!isPrivate && !isVisited) {
                e.currentTarget.style.boxShadow = `0 4px 16px ${tabConfig.color}40`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isPrivate && !isVisited) {
                e.currentTarget.style.boxShadow = `0 2px 8px ${tabConfig.color}30`;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <ExternalLink size={13} />
            {isPrivate ? t('viralNiches.private_label') : (isVisited ? t('viralNiches.visited') : t('viralNiches.view_channel'))}
          </a>
        )}
      </div>
    </div>
  );
};

// Mini Stat Box Component for Channel Card
const MiniStatBox = ({ icon, value, label }) => (
  <div style={{ textAlign: 'center', flex: 1 }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      color: 'var(--dp-text-primary)',
      fontWeight: '700',
      fontSize: '15px',
      letterSpacing: '-0.3px',
    }}>
      <span style={{ color: 'var(--dp-text-tertiary)', opacity: 0.7 }}>{icon}</span>
      {value}
    </div>
    <p style={{
      fontSize: '10px',
      color: 'var(--dp-text-tertiary)',
      margin: '3px 0 0',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>{label}</p>
  </div>
);

// Stat Box Component
const StatBox = ({ label, value }) => (
  <div style={{
    padding: '12px',
    background: 'var(--dp-bg-card)',
    textAlign: 'center'
  }}>
    <p style={{
      fontSize: '16px',
      fontWeight: '700',
      color: 'var(--dp-text-primary)',
      margin: '0 0 2px 0'
    }}>
      {value}
    </p>
    <p style={{
      fontSize: '11px',
      color: 'var(--dp-text-secondary)',
      margin: 0
    }}>
      {label}
    </p>
  </div>
);

// Category Section Component (for "All" tab)
const CategorySection = ({ category: rawCategory, data, expanded, onToggle, formatNumber, searchTerm, isSuperAdmin, onDelete, visitedChannels, onVisit, isPrivate }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const CHANNELS_PER_PAGE = 50;

  // Normalizar category — pode vir como array JSON, objeto, ou string do backend
  const category = Array.isArray(rawCategory)
    ? (rawCategory[0] || 'Outros')
    : (typeof rawCategory === 'object' && rawCategory !== null)
      ? JSON.stringify(rawCategory)
      : String(rawCategory || 'Outros');

  const filteredChannels = (data?.channels || []).filter(channel => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return String(channel.title || '').toLowerCase().includes(search);
  });

  // Reset page when category collapses or search changes
  // IMPORTANTE: este hook precisa vir ANTES de qualquer early return
  // (Rules of Hooks — hooks devem ser chamados na mesma ordem em todo render)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [expanded, searchTerm]);

  if (searchTerm && filteredChannels.length === 0) return null;

  // Traduz a categoria
  const translatedCategory = translateCategory(category, t);

  // Paginação
  const totalPages = Math.ceil(filteredChannels.length / CHANNELS_PER_PAGE);
  const startIndex = (currentPage - 1) * CHANNELS_PER_PAGE;
  const endIndex = startIndex + CHANNELS_PER_PAGE;
  const channelsToShow = filteredChannels.slice(startIndex, endIndex);

  return (
    <div style={{
      background: 'var(--dp-bg-card)',
      border: '1px solid var(--dp-border-subtle)',
      borderRadius: '16px',
      overflow: 'hidden'
    }}>
      <div
        onClick={onToggle}
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          background: expanded ? 'var(--dp-bg-input)' : 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Setinha igual à Lista de Nichos */}
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'color-mix(in srgb, var(--dp-blue-500) 12%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.3s'
          }}>
            <ChevronRight 
              size={20} 
              style={{ 
                color: 'var(--dp-blue-500)',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }} 
            />
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--dp-text-primary)', margin: 0 }}>
              {isPrivate ? '•••••••••' : String(translatedCategory)}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '4px 0 0 0' }}>
              {isPrivate ? '••• canais • ••• views' : String(t('viralNiches.channels_views', { count: Number(data.count) || 0, views: formatNumber(data.total_views) }))}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            padding: '6px 14px',
            background: 'color-mix(in srgb, var(--dp-blue-500) 12%, transparent)',
            color: 'var(--dp-blue-500)',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {isPrivate ? '•••' : (searchTerm ? filteredChannels.length : Number(data.count) || 0)}
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid var(--dp-border-subtle)'
        }}>
          {/* Grid de canais */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px'
          }}>
            {channelsToShow.map(channel => (
              <MiniChannelCard
                key={channel.id}
                channel={channel}
                formatNumber={formatNumber}
                isSuperAdmin={isSuperAdmin}
                onDelete={onDelete}
                isVisited={isChannelVisited(channel.channel_id, visitedChannels || [])}
                onVisit={onVisit}
                isPrivate={isPrivate}
              />
            ))}
          </div>
          
          {/* Paginação interna */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid var(--dp-border-subtle)'
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.max(1, p - 1)); }}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--dp-border-subtle)',
                  background: currentPage === 1 ? 'transparent' : 'var(--dp-blue-500)',
                  color: currentPage === 1 ? 'var(--dp-text-secondary)' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                {t('viralNiches.previous')}
              </button>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Mostrar primeira, última, atual e adjacentes
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, idx, arr) => {
                    // Adicionar ellipsis se houver gap
                    const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span style={{ color: 'var(--dp-text-secondary)', padding: '0 4px' }}>...</span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setCurrentPage(page); }}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            border: page === currentPage ? 'none' : '1px solid var(--dp-border-subtle)',
                            background: page === currentPage ? 'var(--dp-blue-500)' : 'transparent',
                            color: page === currentPage ? 'white' : 'var(--dp-text-primary)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: page === currentPage ? '600' : '400'
                          }}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.min(totalPages, p + 1)); }}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--dp-border-subtle)',
                  background: currentPage === totalPages ? 'transparent' : 'var(--dp-blue-500)',
                  color: currentPage === totalPages ? 'var(--dp-text-secondary)' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                {t('viralNiches.next_page')}
              </button>
              
              <span style={{
                marginLeft: '12px',
                fontSize: '12px',
                color: 'var(--dp-text-secondary)'
              }}>
                {String(t('viralNiches.page_range', { start: startIndex + 1, end: Math.min(endIndex, filteredChannels.length), total: filteredChannels.length }))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Mini Channel Card (for grouped view)
const MiniChannelCard = ({ channel, formatNumber, isSuperAdmin, onDelete, isVisited, onVisit, isPrivate }) => {
  const { t } = useTranslation();
  const handleViewChannel = () => {
    if (onVisit) onVisit(channel.channel_id);
  };
  
  return (
    <div style={{
      background: 'var(--dp-bg-input)',
      border: '1px solid var(--dp-border-subtle)',
      borderRadius: '12px',
      padding: '12px',
      display: 'flex',
      gap: '10px',
      alignItems: 'center'
    }}>
      <img
        src={isPrivate ? `https://ui-avatars.com/api/?name=?&background=888888&size=88&bold=true` : getChannelThumbnail(channel)}
        alt={isPrivate ? t('viralNiches.view_channel') : channel.title}
        onError={(e) => {
          e.target.src = `https://ui-avatars.com/api/?name=${isPrivate ? '?' : (channel.title || 'C')[0]}&background=random&size=88&bold=true`;
        }}
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          objectFit: 'cover',
          filter: isPrivate ? 'blur(2px)' : 'none'
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <h4 style={{
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--dp-text-primary)',
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {isPrivate ? '••••••••••' : String(channel.title || t('viralNiches.channel_no_name'))}
          </h4>
          {channel.is_monetized && (
            <DollarSign size={12} style={{ color: '#10B981', flexShrink: 0 }} />
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--dp-text-secondary)', marginTop: '4px' }}>
          <span>{isPrivate ? '••• views' : `${formatNumber(channel.total_views)} ${String(t('viralNiches.views_label'))}`}</span>
          <span>{isPrivate ? '••• subs' : `${formatNumber(channel.subscribers)} ${String(t('viralNiches.subs_label'))}`}</span>
        </div>
      </div>
      <a
        href={isPrivate ? '#' : ensureHttps(channel.channel_url)}
        target={isPrivate ? '_self' : '_blank'}
        rel="noopener noreferrer"
        onMouseDown={isPrivate ? (e) => e.preventDefault() : handleViewChannel}
        onClick={isPrivate ? (e) => { e.preventDefault(); toast.info(t('viralNiches.disable_private_mode')); } : undefined}
        style={{
          padding: '6px',
          background: isPrivate ? '#888' : (isVisited ? '#888' : 'color-mix(in srgb, var(--dp-blue-500) 12%, transparent)'),
          borderRadius: '8px',
          color: isPrivate ? '#fff' : (isVisited ? '#fff' : 'var(--dp-blue-500)'),
          display: 'flex',
          cursor: isPrivate ? 'not-allowed' : 'pointer',
          opacity: isPrivate ? 0.5 : 1
        }}
      >
        <ExternalLink size={16} />
      </a>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ isSuperAdmin, onUpload, isRemovedTab }) => {
  const { t } = useTranslation();
  return (
    <div style={{
      textAlign: 'center',
      padding: '60px 20px',
      background: 'var(--dp-bg-card)',
      borderRadius: '16px',
      border: '1px solid var(--dp-border-subtle)'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        margin: '0 auto 24px',
        background: isRemovedTab ? 'linear-gradient(135deg, #6B7280, #4B5563)' : 'linear-gradient(135deg, #EF4444, #F59E0B)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {isRemovedTab ? <Trash2 size={40} style={{ color: 'white' }} /> : <Flame size={40} style={{ color: 'white' }} />}
      </div>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        color: 'var(--dp-text-primary)',
        margin: '0 0 12px 0'
      }}>
        {isRemovedTab ? t('viralNiches.no_removed_channels') : t('viralNiches.no_channels')}
      </h3>
      <p style={{
        fontSize: '14px',
        color: 'var(--dp-text-secondary)',
        margin: '0 0 24px 0',
        maxWidth: '400px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        {isRemovedTab
          ? t('viralNiches.removed_channels_appear')
          : isSuperAdmin
            ? t('viralNiches.import_hint_admin')
            : t('viralNiches.channels_available_hint')}
      </p>
      {isSuperAdmin && !isRemovedTab && (
        <button
          onClick={onUpload}
          data-testid="upload-json-empty-btn"
          style={{
            padding: '14px 28px',
            background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <FileJson size={20} />
          {t('viralNiches.import_json_file')}
        </button>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ uploading, onUpload, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="dp-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px',
      animation: 'dp-modal-overlay 0.2s ease'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--dp-bg-glass)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: '16px',
        padding: '28px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid var(--dp-border-medium)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
            {t('viralNiches.import_channels')}
          </h3>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{
          background: 'var(--dp-bg-input)',
          border: '2px dashed var(--dp-border-subtle)',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <FileJson size={48} style={{ color: 'var(--dp-blue-500)', marginBottom: '16px' }} />
          <p style={{ color: 'var(--dp-text-primary)', fontSize: '15px', fontWeight: 600, margin: '0 0 8px 0', fontFamily: 'var(--dp-font)' }}>
            {t('viralNiches.select_json')}
          </p>
          <p style={{ color: 'var(--dp-text-secondary)', fontSize: '13px', margin: '0 0 20px 0', fontFamily: 'var(--dp-font)' }}>
            {t('viralNiches.exported_file_desc')}
          </p>
          <label style={{
            padding: '10px 20px',
            background: uploading ? 'var(--dp-border-subtle)' : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600))',
            color: '#fff',
            borderRadius: '10px',
            fontWeight: 600,
            cursor: uploading ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'var(--dp-font)'
          }}>
            {uploading ? (
              <>
                <Loader size={18} className="animate-spin" />
                {t('viralNiches.processing')}
              </>
            ) : (
              <>
                <Upload size={18} />
                {t('viralNiches.select_file')}
              </>
            )}
            <input
              type="file"
              accept=".json"
              onChange={onUpload}
              disabled={uploading}
              style={{ display: 'none' }}
              data-testid="file-input"
            />
          </label>
        </div>

        <div style={{
          background: 'color-mix(in srgb, var(--dp-blue-500) 8%, transparent)',
          borderRadius: '10px',
          padding: '14px'
        }}>
          <p style={{ fontSize: '13px', color: 'var(--dp-blue-500)', margin: 0, fontWeight: 500, fontFamily: 'var(--dp-font)' }}>
            <strong>{t('viralNiches.smart_system_title')}</strong> {t('viralNiches.smart_system_desc')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NichosViraisV2Page;
